import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'

// §0.4 methodology gating: validatedRunsCount = 0 → architecture_only mode
// This page renders architecture_only content — no accuracy numbers or calibration percentages
// until at least 20 validated pre-reads are recorded (checked at render time via data-methodology-mode)

export function ResearchMethodologyPage() {
  const validatedRunsCount = 0
  const renderMode = validatedRunsCount >= 20 ? 'with_validation_data' : 'architecture_only'

  return (
    <div className="min-h-screen bg-paper" data-methodology-mode={renderMode}>
      <SiteNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 space-y-12">

        <div className="space-y-4">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">Research methodology</p>
          <h1 className="text-4xl font-medium text-deep-ink leading-tight" style={{ letterSpacing: '-0.02em' }}>
            How evidence-backed pre-reads work
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            This page describes the architecture behind Echosome pre-reads: how evidence is stored, how checks are structured, how confidence is derived, and how human validation closes the loop.
          </p>
          {renderMode === 'architecture_only' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <strong>Note:</strong> Calibration accuracy figures are not published yet. We will update this page as validated pre-reads accumulate. We do not publish accuracy claims without real outcomes to support them.
            </div>
          )}
        </div>

        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">1. Evidence layer</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            All uploaded research is parsed and stored as structured evidence: quotes, Likert distributions, segment metadata, and outcome markers (where provided). Every claim in a pre-read is attributed to a specific row or passage in the uploaded evidence.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            Coverage is tracked per segment. Segments with fewer than 30 distinct respondents are flagged. When a pre-read references a segment with thin coverage, the output includes an explicit coverage warning rather than suppressing the uncertainty.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">2. Multiple independent checks</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Rather than producing a single AI answer, Echosome runs multiple independent reasoning passes against the evidence. Each pass uses a different analytical angle: theme alignment, sentiment distribution, behavioral contrast, segment divergence, outlier scan, literature cross-reference, and recency weighting.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            Each pass independently predicts a direction with a confidence range. The aggregate result shows where passes agree and where they disagree. Disagreement is a primary signal for routing to human validation — not a failure to be hidden.
          </p>
          <p className="text-sm text-slate-500 text-xs">
            Architecture note: The multi-pass design is adapted from the Stanford / Google generative-agents framework (Park et al., UIST 2023), applied to first-party research evidence rather than simulated social behavior.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">3. Confidence and uncertainty</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Confidence is derived from two sources: evidence coverage (how many respondents support a claim) and pass agreement (how many independent checks agree on a direction). High confidence requires both.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            Echosome does not suppress uncertainty. If coverage is thin or passes disagree, the pre-read shows the disagreement and identifies which segments need fresh evidence or human validation.
          </p>
          {renderMode === 'architecture_only' && (
            <p className="text-sm text-slate-400 italic">
              Calibration accuracy by question type and segment will be published here as validated outcomes accumulate (minimum 20 validated pre-reads required).
            </p>
          )}
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">4. Human validation loop</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            When a pre-read identifies segments with weak evidence or high disagreement, Echosome generates a minimal screener targeting those specific segments. You export the screener and field it through your panel provider. Results are uploaded back and the pre-read re-runs with updated evidence.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            Echosome does not provide a built-in panel. Human validation recruits real people through providers you already work with.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">5. Learning record</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Each completed validation cycle (pre-read → human field → import results) creates a prediction-outcome record. Over time, these records show which reasoning passes have historically aligned with human outcomes for different question types and segments.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            This is the learning record: not a confidence score assigned by the model, but an empirical track record derived from real prediction-vs-outcome comparisons in your workspace.
          </p>
        </section>

        <section className="space-y-4" id="twin-panel-methodology">
          <h2 className="text-xl font-medium text-deep-ink">6. Twin Panel — per-respondent 1:1 modeling</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            For studies that require individual-level prediction rather than segment-level aggregation, Echosome builds a Twin Panel: one digital twin per respondent in the uploaded CSV.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Backtest process:</strong> Before any twin enters a bulk query, accuracy is measured with a held-out test set. A deterministic 80/20 split is computed per twin seeded on <code>(twin_id + upload_id)</code> — ensuring identical inputs always produce identical holdout splits. All 8 reasoning passes run against the 20% held-out questions. Correct predictions vs. total held-out questions yields the raw accuracy score.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Wilson confidence interval:</strong> Accuracy is reported using the Wilson score interval at 95% confidence (z = 1.96). This interval is conservative for small holdout sets — it shrinks toward 0.5 when total held-out questions are few. A twin requires at least 5 holdout questions to receive a <code>validated</code> status; fewer than 5 yields <code>low_confidence</code>.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Status thresholds:</strong> Validated: Wilson lower bound ≥ 0.4 AND ≥ 5 holdout questions. Low confidence: Wilson lower &lt; 0.4 OR &lt; 5 holdout questions. Excluded: insufficient data to backtest.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Two-layer config selection for bulk queries:</strong> Layer 1 selects the top-K configs by Wilson lower bound on past accuracy (personal baseline). Layer 2 adjusts for question-keyword proximity — if the new question closely resembles questions where a specific config historically agreed with the human answer, that config is upweighted. This prevents uniform config selection from dominating when evidence about a specific topic is concentrated in fewer passes.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Bulk query aggregation:</strong> Predictions from all validated twins are aggregated to a distribution. Per-segment cross-tabs are computed from demographic columns in the original upload. Dissent (within-segment variance exceeding a threshold) is flagged on the result page. All source quotes from twin responses carry <code>source_text_is_untrusted: true</code> markers — they are evidence, not verified statements.
          </p>
          {renderMode === 'architecture_only' && (
            <p className="text-sm text-slate-400 italic">
              Twin Panel aggregate accuracy figures (mean, p25, p50, p75, p90) will be published here as real deployment data accumulates. Current figures shown in the app are per-project, not cross-project benchmarks.
            </p>
          )}
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">7. What Echosome does not claim</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Echosome does not claim causal proof. Pre-reads are predictive research support, not causal analysis.</li>
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Echosome does not generate synthetic respondents. All predictions are grounded in uploaded evidence from real humans.</li>
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Echosome does not replace legal or compliance review. Final review stays with your legal and compliance teams.</li>
            <li className="flex items-start gap-2"><span className="text-echo-indigo mt-0.5 shrink-0">→</span>Echosome does not publish accuracy percentages without validated outcomes to support them.</li>
          </ul>
        </section>

        <div className="border border-border-gray rounded-xl p-5 space-y-3">
          <p className="text-sm text-slate-600">
            Questions about methodology, data handling, or enterprise use? Start with the security page or talk to us directly.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/security"><Button variant="outline" size="sm">Security overview</Button></Link>
            <Link to="/app/demo"><Button variant="primary" size="sm">Try a live demo</Button></Link>
          </div>
        </div>

      </main>
      <SiteFooter />
    </div>
  )
}
